import { Component, computed, effect, inject, input, OnDestroy } from '@angular/core';
import { translate, translateSignal, TranslocoDirective } from '@jsverse/transloco';
import { ActivatedRoute, createUrlTreeFromSnapshot, Router, RouterOutlet } from '@angular/router';
import { Dialog } from '@shared/components/dialog/dialog';
import { AssetChangeStateComponentState } from '@features/asset-change-state/state/asset-state-change-component-state';
import { Nullable } from '@shared/models/types';
import { Button } from '@shared/directives/button/button';
import { DialogFooterBase } from '@shared/components/dialog/dialog-footer-base';
import { Steps } from '@shared/components/steps/steps';
import { TaStepItem } from '@shared/components/steps/steps.model';
import { getFromStorage, removeFromStorage } from '@shared/utility/session-local-storage';
import { StorageKeysEnum } from '@shared/utility/storage-keys';
import { injectApplyChangeStateMutation } from '@core/devices/state/device.commands';
import { Store } from '@ngrx/store';
import { AssetDetailApiActions } from '@features/asset-detail/store/asset-detail.actions';
import { CoreDefaultActions } from '@core/state/core.actions';
import {
  DeviceStateWizardJobCloseDTO,
  DeviceStateWizardScheduleDeleteDTO,
  DeviceStateWizardTicketCloseDTO
} from '@core/devices/device.model';
import { AlertMgr } from '@core/alert/alert-mgr';
import { AssetsApiActions } from '@features/assets/state/assets.actions';
import { injectDeviceWizardJobQuery, injectDeviceWizardScheduleQuery } from '@core/job/state/job-type.queries';
import { injectJobEvents } from '@core/job/store/job-events';
import { injectDeviceWizardTicketQuery } from '@core/ticket/state/ticket.queries';
import { AssetChangeStateParams } from '@features/asset-change-state/model/asset-change-state.model';
import { OutletLoader } from '@shared/components/loader/outlet-loader';

@Component({
  selector: 'ta-asset-change-state-page',
  template: `
    <ng-container *transloco="let translate">
      <ta-dialog
        dialogCssClass="w-full max-w-340 min-h-200"
        [headerTitle]="translate('changeState')"
        [showFooter]="true"
        [showHeader]="true"
        [showLoader]="this.applyChangeStateMutation.isPending() || this.isLoading()"
        [visible]="true"
        (onhide)="this.goBack(true)"
      >
        <ng-template #bodyTemplate>
          <div class="flex-none py-4">
            <nav
              taSteps
              [activeIdx]="this.assetChangeStateComponentState.currentStep()"
              [dataSource]="this.stepsDataSource()"
            ></nav>
          </div>
          <div class="flex flex-col flex-1 min-h-0 h-full overflow-y-auto my-4 relative">
            <router-outlet />
            <ta-outlet-loader />
          </div>
        </ng-template>
        <ng-template #footerTemplate>
          <div cssClass="gap-2" taDialogFooterBase>
            <button taButton type="button" variant="secondary" (click)="this.goBack(true)">
              {{ translate('cancel') }}
            </button>
            @if (this.assetChangeStateComponentState.currentStep() > 0) {
              <button taButton type="button" (click)="this.goBack(false)">
                {{ translate('back') }}
              </button>
            }
            @if (
              this.isNotReactivationWizard()
                ? this.assetChangeStateComponentState.currentStep() < 3
                : this.assetChangeStateComponentState.currentStep() < 2
            ) {
              <button taButton type="button" (click)="this.nextHandler()">
                {{ translate('next') }}
              </button>
            }
            @if (
              this.isNotReactivationWizard()
                ? this.assetChangeStateComponentState.currentStep() === 4
                : this.assetChangeStateComponentState.currentStep() === 3
            ) {
              <button
                taButton
                type="button"
                [disabled]="this.assetChangeStateComponentState.canNotConfirm()"
                (click)="this.confirm()"
              >
                {{ translate('confirm') }}
              </button>
            } @else {
              <button taButton type="button" (click)="this.goToConfirm()">
                {{ translate('goToConfirm') }}
              </button>
            }
          </div>
        </ng-template>
      </ta-dialog>
    </ng-container>
  `,
  host: { class: 'flex-col-layout' },
  providers: [AssetChangeStateComponentState],
  imports: [TranslocoDirective, RouterOutlet, Dialog, Button, DialogFooterBase, Steps, OutletLoader]
})
export class AssetChangeStatePage implements OnDestroy {
  private readonly jobEvents = injectJobEvents();

  // Parametri provenienti dalla rotta
  protected readonly dId = input(-1);
  protected readonly cId = input(-1);
  protected readonly outlet = input<Nullable<'modal' | 'second-modal'>>();
  protected readonly router = inject(Router);
  protected readonly store = inject(Store);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly alertService = inject(AlertMgr);
  protected readonly isNotReactivationWizard = computed(
    () => this.deviceParam()?.statoDisp === 'UnderMaintenance' || this.deviceParam()?.statoDisp === 'Discontinued'
  );
  protected readonly scheduleForDeviceQuery = injectDeviceWizardScheduleQuery(
    computed(() => this.dId()),
    computed(() => {
      return this.isNotReactivationWizard();
    })
  );
  protected readonly jobForDeviceQuery = injectDeviceWizardJobQuery(
    computed(() => this.dId()),
    []
  );
  protected readonly ticketForDeviceQuery = injectDeviceWizardTicketQuery(computed(() => this.dId()));

  protected readonly deviceParam = getFromStorage<Nullable<AssetChangeStateParams>>(
    'sessionStorage',
    StorageKeysEnum.changeAssetStateParams
  );

  protected readonly isLoading = computed(() => {
    return (
      this.scheduleForDeviceQuery.isPending() ||
      this.jobForDeviceQuery.isPending() ||
      this.ticketForDeviceQuery.isPending()
    );
  });

  protected readonly stepsToMaintenanceTranslated = translateSignal([
    'overview',
    'manageSchedule',
    'manageJob',
    'manageTicket',
    'summary'
  ] as const);
  protected readonly stepsFromMaintenanceTranslated = translateSignal([
    'overview',
    'manageSchedule',
    'manageScheduledJob',
    'summary'
  ] as const);
  protected readonly stepPathsToMaintenance = ['first', 'second', 'third', 'fourth', 'fifth'] as const;
  protected readonly stepPathsFromMaintenance = ['first', 'second', 'third', 'fifth'] as const;
  protected readonly assetChangeStateComponentState = inject(AssetChangeStateComponentState);
  protected readonly applyChangeStateMutation = injectApplyChangeStateMutation();

  protected readonly stepsDataSource = computed<TaStepItem[]>(() => {
    let result: TaStepItem[];
    if (this.isNotReactivationWizard()) {
      const [overview, manageSchedule, manageJob, manageTicket, summary] = this.stepsToMaintenanceTranslated();
      result = [
        { label: overview },
        { label: manageSchedule },
        { label: manageJob },
        { label: manageTicket },
        { label: summary }
      ];
    } else {
      const [overview, manageSchedule, manageJob, summary] = this.stepsFromMaintenanceTranslated();
      result = [{ label: overview }, { label: manageSchedule }, { label: manageJob }, { label: summary }];
    }
    return result;
  });

  constructor() {
    effect(() => {
      const schedules = this.scheduleForDeviceQuery.data();
      if (schedules) {
        this.assetChangeStateComponentState.loadedSchedules.set(schedules);
      }
    });

    effect(() => {
      const jobs = this.jobForDeviceQuery.data();
      if (jobs) {
        this.assetChangeStateComponentState.loadedJobs.set(jobs);
      }
    });

    effect(() => {
      const tickets = this.ticketForDeviceQuery.data();
      if (tickets) {
        this.assetChangeStateComponentState.loadedTickets.set(tickets);
      }
    });

    effect(() => {
      const device = this.deviceParam();
      if (device) {
        this.assetChangeStateComponentState.device.set(device);
      }
    });
  }

  public ngOnDestroy(): void {
    removeFromStorage('sessionStorage', StorageKeysEnum.changeAssetStateParams);
  }

  protected nextHandler(): void {
    const nextStep = this.assetChangeStateComponentState.currentStep() + 1;
    let nextPath: string;
    if (this.isNotReactivationWizard()) {
      nextPath = this.stepPathsToMaintenance[nextStep];
    } else {
      nextPath = this.stepPathsFromMaintenance[nextStep];
    }

    this.router
      .navigate([{ outlets: { [this.outlet() ?? 'modal']: ['asset-change-state', nextPath] } }], {
        queryParamsHandling: 'merge'
      })
      .then((navigated) => {
        if (navigated) {
          this.assetChangeStateComponentState.currentStep.set(nextStep);
        }
      });
  }

  protected confirm() {
    const device = this.assetChangeStateComponentState.device();
    const schedules = this.assetChangeStateComponentState.getListsFromScheduleChangeMap();
    const jobs = this.assetChangeStateComponentState.getListsFromJobChangeMap();
    const tickets = this.assetChangeStateComponentState.getListsFromTicketChangeMap();

    const schedulesToDelete = schedules.toDelete.map((s) => {
      return {
        id: s.id,
        deleteConcrete: s.deleteJobs
      } as DeviceStateWizardScheduleDeleteDTO;
    });

    const schedulesToChange = schedules.toChange.map((s) => {
      return {
        id: s.id,
        stateExecutionJob: s.stateExecutionJob,
        reschedule: s.reschedule ?? false,
        skipMaintenancePeriod: s.considerMaintenance ?? false
      };
    });

    const jobsToChange = jobs.toChange.map((j) => {
      return {
        id: j.id,
        stateExecutionJob: j.stateExecutionJob,
        reschedule: false,
        skipMaintenancePeriod: false
      };
    });

    const jobsToClose = jobs.toClose.map((j) => {
      return {
        id: j.id,
        executor: j.closed?.executor,
        resultId: j.closed?.resultId,
        executionDate: j.closed?.executionDate,
        endDateAsExecutionDate: j.closed?.endDateAsExecutionDate,
        notes: j.closed?.notes
      } as DeviceStateWizardJobCloseDTO;
    });

    const ticketsToChange = tickets.toChange.map((t) => {
      return {
        id: t.id,
        stateId: t.stateId
      };
    });

    const ticketsToClose = tickets.toClose.map((t) => {
      return {
        id: t.id,
        content: t.closed?.content,
        mentions: []
      } as DeviceStateWizardTicketCloseDTO;
    });

    // Il wizard tocca schedulazioni e attività in un'unica mutation condivisa: si emette l'evento di
    // dominio corrispondente solo per i lotti che hanno effettivamente cambiamenti in sospeso.
    const hasScheduleChanges = schedulesToChange.length > 0 || schedulesToDelete.length > 0;
    const hasJobChanges = jobsToChange.length > 0 || jobsToClose.length > 0 || jobs.toDelete.length > 0;

    if (device) {
      this.applyChangeStateMutation.mutate(
        {
          id: device.deviceId,
          statoDisp: device.statoDisp,
          lastModificationDate: device.lastModificationDate,
          maintenanceDate: device.maintenanceDate,
          disposalDate: device.disposalDate,
          schedulesToChange: schedulesToChange,
          schedulesToDelete: schedulesToDelete,
          jobsToChange: jobsToChange,
          jobsToClose: jobsToClose,
          jobsToDelete: jobs.toDelete.map((j) => {
            return j.id;
          }),
          ticketsToChange: ticketsToChange,
          ticketsToClose: ticketsToClose,
          ticketsToDelete: tickets.toDelete.map((t) => {
            return t.id;
          })
        },
        {
          onSuccess: () => {
            this.store.dispatch(
              CoreDefaultActions.dispatchActionAndPublishMessageToBroadcastChannel({
                actions: [
                  AssetDetailApiActions.getById({
                    stateId: device.stateId,
                    id: device.deviceId,
                    showLoader: true
                  }),
                  AssetsApiActions.getTreeAssetById({
                    id: device.deviceId,
                    isNew: false
                  })
                ]
              })
            );
            if (hasScheduleChanges) {
              this.jobEvents.scheduleChanged();
            }
            if (hasJobChanges) {
              this.jobEvents.bulkChanged();
            }
          },
          onSettled: () => {
            this.assetChangeStateComponentState.scheduleChange.set(new Map());
            this.assetChangeStateComponentState.ticketChange.set(new Map());
            this.assetChangeStateComponentState.jobChange.set(new Map());
            this.goBack(true);
          }
        }
      );
    }
  }

  protected goToConfirm(): void {
    const lastStep = this.isNotReactivationWizard() ? this.stepPathsToMaintenance[4] : this.stepPathsFromMaintenance[3];

    this.router
      .navigate([{ outlets: { [this.outlet() ?? 'modal']: ['asset-change-state', lastStep] } }], {
        queryParamsHandling: 'merge'
      })
      .then((navigated) => {
        if (navigated) {
          this.isNotReactivationWizard()
            ? this.assetChangeStateComponentState.currentStep.set(4)
            : this.assetChangeStateComponentState.currentStep.set(3);
        }
      });
  }

  protected goBack(forceClose: boolean): void {
    const currentStep = this.assetChangeStateComponentState.currentStep();
    if (currentStep === 0 || forceClose) {
      const scheduleLists = this.assetChangeStateComponentState.getListsFromScheduleChangeMap();
      const jobLists = this.assetChangeStateComponentState.getListsFromJobChangeMap();
      const ticketLists = this.assetChangeStateComponentState.getListsFromTicketChangeMap();
      const hasChanges = [
        scheduleLists.toChange,
        scheduleLists.toDelete,
        jobLists.toChange,
        jobLists.toDelete,
        jobLists.toClose,
        ticketLists.toChange,
        ticketLists.toDelete,
        ticketLists.toClose
      ].some((list) => list.length > 0);
      if (hasChanges) {
        const [title, msg] = translate(['changeState', 'confirmCancelChangeStateWizard']);
        this.alertService.showConfirm(title, msg, null, null, (res) => {
          if (res) {
            this.closeChangeStateWizard();
          }
        });
      } else {
        this.closeChangeStateWizard();
      }
    } else {
      const prevStep = currentStep - 1;
      let prevPath: string;
      if (this.isNotReactivationWizard()) {
        prevPath = this.stepPathsToMaintenance[prevStep];
      } else {
        prevPath = this.stepPathsFromMaintenance[prevStep];
      }
      this.router
        .navigate([{ outlets: { [this.outlet() ?? 'modal']: ['asset-change-state', prevPath] } }], {
          queryParamsHandling: 'merge'
        })
        .then((navigated) => {
          if (navigated) {
            this.assetChangeStateComponentState.currentStep.set(prevStep);
          }
        });
    }
  }

  private closeChangeStateWizard(): void {
    removeFromStorage('sessionStorage', StorageKeysEnum.changeAssetStateParams);
    this.assetChangeStateComponentState.currentStep.set(0);

    const tree = createUrlTreeFromSnapshot(this.activatedRoute.snapshot, []);
    this.router.navigate(
      [{ outlets: { [this.outlet() ?? 'modal']: null, primary: tree.root.children['primary'].toString() } }],
      { queryParamsHandling: 'merge' }
    );

    this.assetChangeStateComponentState.scheduleChange.set(new Map());
    this.assetChangeStateComponentState.ticketChange.set(new Map());
    this.assetChangeStateComponentState.jobChange.set(new Map());
  }
}
